<template>
  <div class="pointer-events-none absolute inset-0 h-full w-full overflow-hidden">
    <svg class="h-full w-full" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <g v-for="circle in circles" :key="circle.id" class="confetti-particle" :style="particleStyle(circle)">
        <circle cx="0" cy="0" :r="circle.size" :fill="circle.color" />
      </g>

      <g v-for="triangle in triangles" :key="triangle.id" class="confetti-particle" :style="particleStyle(triangle)">
        <polygon :points="trianglePoints(triangle.size)" :fill="triangle.color" />
      </g>

      <g v-for="stroke in strokes" :key="stroke.id" class="confetti-particle" :style="particleStyle(stroke)">
        <line :x1="-stroke.size / 2" y1="0" :x2="stroke.size / 2" y2="0" :stroke="stroke.color" :stroke-width="stroke.width" stroke-linecap="round" />
      </g>

      <g v-if="ribbonsAnimationReady">
        <g v-for="ribbon in ribbons" :key="ribbon.id" class="confetti-ribbon" :transform="ribbonTransform(ribbon)">
          <path
            :d="ribbon.path"
            fill="none"
            :stroke="ribbon.color"
            :stroke-width="ribbon.width"
            stroke-linecap="round"
            stroke-linejoin="round"
            pathLength="100"
            stroke-dasharray="0 100"
            stroke-dashoffset="0">
            <animate attributeName="stroke-dasharray" :values="ribbonDashArrayValues(ribbon)" :keyTimes="ribbonKeyTimes(ribbon)" dur="1.76s" repeatCount="1" fill="freeze" />
            <animate attributeName="stroke-dashoffset" :values="ribbonDashOffsetValues(ribbon)" :keyTimes="ribbonKeyTimes(ribbon)" dur="1.76s" repeatCount="1" fill="freeze" />
          </path>
        </g>
      </g>
    </svg>
  </div>
</template>

<script setup>
import { nextTick, onMounted, ref } from 'vue'

const colors = {
  blue: '#19b1ff',
  cyan: '#3dd2f9',
  deepBlue: '#283778',
  green: '#45e8a3',
  purple: '#7d3bc0',
  red: '#ff4848',
  yellow: '#ffb400',
}

const ribbonFrameCount = 44
const ribbonsAnimationReady = ref(false)

onMounted(async () => {
  await nextTick()

  requestAnimationFrame(() => {
    ribbonsAnimationReady.value = true
  })
})

const circles = [
  { id: 'blue-04', start: [1186, -230], end: [1186, 1206], size: 11, color: colors.blue, delay: 1.28, duration: 2.04, rotate: 140, scaleFrom: 0.45, scaleTo: 0.9 },
  { id: 'red-05', start: [795, -25], end: [795, 1411], size: 12, color: colors.red, delay: 0.76, duration: 2.32, rotate: -90, scaleFrom: 0.5, scaleTo: 0.95 },
  { id: 'yellow-04', start: [108, -39], end: [108, 1397], size: 17, color: colors.yellow, delay: 1.04, duration: 1.88, rotate: 80, scaleFrom: 0.55, scaleTo: 1 },
  { id: 'purple-05', start: [449, -228], end: [449, 1208], size: 12, color: colors.purple, delay: 1.28, duration: 2.32, rotate: 160, scaleFrom: 0.45, scaleTo: 0.95 },
  { id: 'blue-03', start: [1430, -242], end: [1430, 1194], size: 11, color: colors.blue, delay: 1.56, duration: 2.04, rotate: -130, scaleFrom: 0.45, scaleTo: 0.9 },
  { id: 'red-04', start: [1039, -37], end: [1039, 1399], size: 12, color: colors.red, delay: 0.64, duration: 2.32, rotate: 120, scaleFrom: 0.5, scaleTo: 0.95 },
  { id: 'yellow-03', start: [352, -51], end: [352, 1385], size: 17, color: colors.yellow, delay: 1.2, duration: 2.32, rotate: -110, scaleFrom: 0.55, scaleTo: 1 },
  { id: 'purple-04', start: [693, -240], end: [693, 1196], size: 12, color: colors.purple, delay: 0.92, duration: 2.32, rotate: 150, scaleFrom: 0.45, scaleTo: 0.95 },
  { id: 'blue-side-01', start: [1300, 212], end: [1144, 1156], size: 10, color: colors.blue, delay: 0, duration: 2.56, rotate: 90, scaleFrom: 0.25, scaleTo: 0.55 },
  { id: 'purple-side-01', start: [1020, 116], end: [928, 1152], size: 13, color: colors.purple, delay: 0, duration: 2.6, rotate: -120, scaleFrom: 0.5, scaleTo: 0.7 },
  { id: 'red-side-02', start: [1992, 1216], end: [1322, 1140], size: 10, color: colors.red, delay: 0.36, duration: 2.6, rotate: 90, scaleFrom: 0.25, scaleTo: 0.35 },
  { id: 'yellow-side-01', start: [1732, 1216], end: [1096, 1140], size: 14, color: colors.yellow, delay: 0.2, duration: 2.6, rotate: -140, scaleFrom: 0.35, scaleTo: 0.65 },
]

const triangles = [
  { id: 'triangle-01', start: [1654, -58], end: [1654, 1222], size: 28, color: colors.green, delay: 1.08, duration: 3.52, rotate: 420, scaleFrom: 0.6, scaleTo: 0.9 },
  { id: 'triangle-02', start: [1414, -58], end: [1414, 1222], size: 28, color: colors.yellow, delay: 0.92, duration: 3.52, rotate: -360, scaleFrom: 0.6, scaleTo: 0.9 },
  { id: 'triangle-03', start: [1930, 1130], end: [902, 1148], size: 24, color: colors.purple, delay: 1.04, duration: 3, rotate: 320, scaleFrom: 0.3, scaleTo: 0.25 },
  { id: 'triangle-04', start: [1940, 1124], end: [1044, 1148], size: 34, color: colors.blue, delay: 0.64, duration: 3.4, rotate: -260, scaleFrom: 0.8, scaleTo: 0.5 },
  { id: 'triangle-05', start: [1988, 1208], end: [1416, -248], size: 36, color: colors.red, delay: 0.52, duration: 3.52, rotate: 390, scaleFrom: 0.8, scaleTo: 0.5 },
  { id: 'triangle-06', start: [1060, -120], end: [1060, 1180], size: 32, color: colors.deepBlue, delay: 0.28, duration: 3.2, rotate: -340, scaleFrom: 0.8, scaleTo: 0.45 },
]

const strokes = [
  { id: 'frill-01', start: [92, -147], end: [92, 1213], size: 44, width: 10, color: colors.blue, delay: 0.08, duration: 3.04, rotate: 220, scaleFrom: 0.9, scaleTo: 0.9 },
  { id: 'frill-02', start: [232, -147], end: [232, 1213], size: 48, width: 10, color: colors.deepBlue, delay: 0.36, duration: 3.04, rotate: -180, scaleFrom: 0.9, scaleTo: 0.9 },
  { id: 'frill-03', start: [528, -59], end: [532, 1413], size: 50, width: 10, color: colors.green, delay: 0.24, duration: 3.04, rotate: 280, scaleFrom: 0.9, scaleTo: 0.9 },
  { id: 'frill-04', start: [728, -59], end: [732, 1413], size: 46, width: 10, color: colors.red, delay: 0.52, duration: 3.04, rotate: -240, scaleFrom: 0.9, scaleTo: 0.9 },
  { id: 'frill-05', start: [1128, -59], end: [1088, 1137], size: 42, width: 10, color: colors.purple, delay: 0.64, duration: 3.04, rotate: 210, scaleFrom: 0.9, scaleTo: 0.9 },
  { id: 'frill-06', start: [1455, -59], end: [1459, 1213], size: 48, width: 10, color: colors.yellow, delay: 0.28, duration: 3.04, rotate: -260, scaleFrom: 0.9, scaleTo: 0.9 },
  { id: 'frill-07', start: [1664, -23], end: [1772, 1177], size: 44, width: 10, color: colors.purple, delay: 0.84, duration: 3.04, rotate: 240, scaleFrom: 0.9, scaleTo: 0.9 },
  { id: 'frill-08', start: [1235, -59], end: [1039, 1237], size: 42, width: 10, color: colors.deepBlue, delay: 0.48, duration: 3.04, rotate: -220, scaleFrom: 0.9, scaleTo: 0.9 },
]

const ribbons = [
  {
    id: 'ribbonYellow01',
    path: 'M -996.479 553.827 C -996.479 553.827 -829.628 489.32 -814.281 359.159 C -798.934 228.997 -955.419 165.825 -768.857 64.753 C -588.695 -32.851 -605.941 160.789 -717.951 160.649 C -829.961 160.507 -784.48 -36.977 -716.405 -79.549 C -628.221 -134.696 -555.337 -97.018 -503.734 -159.43 C -446.546 -228.596 -447.827 -279.335 -491.038 -358.543 C -534.249 -437.751 -744 -608 -963.412 -597.718',
    width: 10,
    color: colors.yellow,
    position: [960, 540],
    anchor: [0, 0],
    rotate: 0,
    endStart: 0,
    startStart: 7,
    endEnd: 33,
    startEnd: 40,
  },
  {
    id: 'ribbonPurple01',
    path: 'M -596 612 C -483.625 414.122 -818.383 476.763 -704 247.999 C -648 136 -552 110 -446 120 C -290.677 134.654 -241.129 54.411 -298 -16 C -340 -68 -528 -42 -430 -158 C -350.091 -252.586 -510.331 -270.356 -482 -330 C -444 -410 -336 -322 -246 -366 C -153.89 -411.031 -126 -602 -308 -652',
    width: 10,
    color: colors.purple,
    position: [960, 540],
    anchor: [0, 0],
    rotate: 0,
    endStart: 5,
    startStart: 8,
    endEnd: 38,
    startEnd: 41,
  },
  {
    id: 'ribbonBlue01',
    path: 'M -1072 420 C -1072 420 -828 516 -688 316 C -548 116 -654.503 -22.331 -484 -136 C -346 -228 -115.881 -62 18 -62 C 196 -62 219.726 -248.3 178 -370 C 154 -440 84 -640 308 -692',
    width: 15,
    color: colors.cyan,
    position: [960, 540],
    anchor: [0, 0],
    rotate: 0,
    endStart: 1,
    startStart: 8,
    endEnd: 34,
    startEnd: 41,
  },
  {
    id: 'ribbonRed01',
    path: 'M 1006 496 C 1006 496 753.331 441.626 688 340 C 634 256 678 172 704 96 C 740.079 -9.462 600 -12 568 46 C 525.654 122.752 590 204 738 200 C 886 196 932.811 -20.897 832 -158 C 732 -294 616 -258 600 -414 C 584 -570 740 -660 832 -664',
    width: 15,
    color: colors.red,
    position: [960, 540],
    anchor: [0, 0],
    rotate: 0,
    endStart: 2,
    startStart: 9,
    endEnd: 35,
    startEnd: 42,
  },
  {
    id: 'ribbonPurple02',
    path: 'M 524 624 C 524 624 362.326 557.812 370 412 C 378 260.001 487.633 307.741 544 236.001 C 654 96 418.093 127.129 462 -40 C 524 -276 56 -62 48 -256 C 34.708 -578.34 -236 -542 -174 -752',
    width: 15,
    color: colors.green,
    position: [960, 540],
    anchor: [0, 0],
    rotate: 0,
    endStart: 3,
    startStart: 10,
    endEnd: 36,
    startEnd: 43,
  },
  {
    id: 'ribbonRed02',
    path: 'M 736 520 C 736 520 875.331 447.626 810 346 C 756 262 882.275 198.987 830 138 C 806 110 734 104 716 140 C 689.407 193.186 750 224 780 226 C 927.726 235.848 936.811 45.103 836 -92 C 736 -228 644 -198 656 -334 C 666.968 -458.305 833.095 -596.42 887.095 -676.42',
    width: 15,
    color: colors.deepBlue,
    position: [655.204, 920.201],
    anchor: [787.204, 316.201],
    rotate: 8.9,
    endStart: 4,
    startStart: 11,
    endEnd: 37,
    startEnd: 44,
  },
]

const particleStyle = (particle) => ({
  '--x-start': `${particle.start[0]}px`,
  '--y-start': `${particle.start[1]}px`,
  '--x-end': `${particle.end[0]}px`,
  '--y-end': `${particle.end[1]}px`,
  '--x-static': `${(particle.start[0] + particle.end[0]) / 2}px`,
  '--y-static': `${Math.min(980, Math.max(90, (particle.start[1] + particle.end[1]) / 2))}px`,
  '--scale-from': particle.scaleFrom ?? 1,
  '--scale-to': particle.scaleTo ?? 1,
  '--rotate-start': '0deg',
  '--rotate-end': `${particle.rotate ?? 180}deg`,
  animationDelay: `${particle.delay}s`,
  animationDuration: `${particle.duration}s`,
})

const trianglePoints = (size) => {
  const height = size * 0.9

  return `0 ${-height} ${size} ${height} ${-size} ${height}`
}

const ribbonTransform = (ribbon) => `translate(${ribbon.position[0]} ${ribbon.position[1]}) rotate(${ribbon.rotate}) translate(${-ribbon.anchor[0]} ${-ribbon.anchor[1]})`

const ribbonFrames = (ribbon) =>
  [0, ribbon.endStart, ribbon.startStart, ribbon.endEnd, ribbon.startEnd, ribbonFrameCount]
    .filter((frame, index, frames) => frame >= 0 && frame <= ribbonFrameCount && frames.indexOf(frame) === index)
    .sort((a, b) => a - b)

const trimProgress = (frame, startFrame, endFrame) => {
  if (frame <= startFrame) return 0
  if (frame >= endFrame) return 100

  return ((frame - startFrame) / (endFrame - startFrame)) * 100
}

const ribbonTrimAtFrame = (ribbon, frame) => {
  const end = trimProgress(frame, ribbon.endStart, ribbon.endEnd)
  const start = trimProgress(frame, ribbon.startStart, ribbon.startEnd)

  return {
    length: Math.max(0, end - start),
    offset: -start,
  }
}

const formatRibbonValue = (value) => Number(value.toFixed(3))

const ribbonKeyTimes = (ribbon) =>
  ribbonFrames(ribbon)
    .map((frame) => formatRibbonValue(frame / ribbonFrameCount))
    .join(';')

const ribbonDashArrayValues = (ribbon) =>
  ribbonFrames(ribbon)
    .map((frame) => `${formatRibbonValue(ribbonTrimAtFrame(ribbon, frame).length)} 100`)
    .join(';')

const ribbonDashOffsetValues = (ribbon) =>
  ribbonFrames(ribbon)
    .map((frame) => formatRibbonValue(ribbonTrimAtFrame(ribbon, frame).offset))
    .join(';')
</script>

<style scoped>
.confetti-particle {
  animation-name: confetti-fall;
  animation-timing-function: cubic-bezier(0.33, 0, 0.83, 0.83);
  animation-iteration-count: infinite;
  animation-fill-mode: both;
  transform-box: fill-box;
  transform-origin: center;
  will-change: transform, opacity;
}

@keyframes confetti-fall {
  0% {
    opacity: 0;
    transform: translate(var(--x-start), var(--y-start)) scale(var(--scale-from)) rotate(var(--rotate-start));
  }

  10% {
    opacity: 1;
  }

  82% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translate(var(--x-end), var(--y-end)) scale(var(--scale-to)) rotate(var(--rotate-end));
  }
}

@media (prefers-reduced-motion: reduce) {
  .confetti-particle {
    animation: none;
  }

  .confetti-particle {
    opacity: 0.55;
    transform: translate(var(--x-static), var(--y-static)) scale(var(--scale-to)) rotate(var(--rotate-end));
  }

  .confetti-ribbon path {
    stroke-dasharray: 100 100;
    stroke-dashoffset: 0;
  }
}
</style>
